import React from 'react'
import { useTranslation } from 'react-i18next'
import { KibChipFilter } from '@chewy/kib-chips-react'
import { KibButtonNew } from '@chewy/kib-controls-react'
import classNames from 'classnames'

import '@chewy/kib-chips-styles/dist/styles/kib-chip-filter.css'
import '@chewy/kib-controls-styles/dist/styles/kib-button-new.css'
import styles from './MultiSectionFiltersFacetsWidget.module.scss'

export interface FilterFacetItem<T = any> {
  disabled?: boolean
  enabled?: boolean
  id: string | number
  label: string
  value: T
}

export interface FilterSectionProps {
  classes?: { chipContainer?: string }
  enableDeselectAllButton?: boolean
  enableSelectAllButton?: boolean
  hideHeader?: boolean
  items: FilterFacetItem[]
  onDeselectSection: (
    sectionKey: string,
    sectionType: 'filter' | 'facet',
  ) => void
  onSelectSection?: (
    sectionKey: string,
    sectionType: 'filter' | 'facet',
  ) => void
  sectionKey: string
  sectionTitle?: string
  sectionType: 'filter' | 'facet'
  toggleHandler: (
    sectionKey: string,
    itemId: string | number,
    enabled: boolean,
  ) => void
}

const FilterSection: React.FC<FilterSectionProps> = ({
  classes: classesProp,
  sectionKey,
  items,
  sectionType,
  sectionTitle,
  toggleHandler,
  enableDeselectAllButton,
  enableSelectAllButton = false,
  hideHeader = false,
  onDeselectSection,
  onSelectSection,
}) => {
  const { t } = useTranslation('Common')
  const hasEnabledItems = items.some((item) => item.enabled)
  const allItemsEnabled =
    items.length > 0 && items.every((item) => item.enabled)

  const shouldShowSelectAll = enableSelectAllButton && !allItemsEnabled

  const shouldEnableDeselectAllButton =
    enableDeselectAllButton && (!enableSelectAllButton || allItemsEnabled)

  const handleButtonClick = () => {
    if (shouldShowSelectAll && onSelectSection) {
      onSelectSection(sectionKey, sectionType)
    } else if (shouldEnableDeselectAllButton) {
      onDeselectSection(sectionKey, sectionType)
    }
  }

  const buttonText = shouldShowSelectAll
    ? t('Common:SELECT_ALL')
    : t('Common:DESELECT_ALL')

  const showButton = enableSelectAllButton || enableDeselectAllButton
  const isButtonDisabled =
    !enableSelectAllButton && enableDeselectAllButton && !hasEnabledItems

  return (
    <div className={styles.section} key={`${sectionType}-${sectionKey}`}>
      {!hideHeader && (
        <div className={styles.sectionHeader}>
          <p className={styles.sectionTitle}>{sectionTitle || sectionKey}</p>
          {showButton && (
            <KibButtonNew
              className={styles.deselectButton}
              disabled={isButtonDisabled}
              emphasis="tertiary"
              size="small"
              theme="action"
              onClick={handleButtonClick}
            >
              {buttonText}
            </KibButtonNew>
          )}
        </div>
      )}
      <div
        className={classNames(styles.chipContainer, classesProp?.chipContainer)}
      >
        {items.map((item) => {
          const isCountZero = /\(\s*0\s*\)/.test(item.label)
          return isCountZero ? (
            <KibChipFilter
              disabled
              className={styles.chipFilter}
              key={item.id}
              toggle={() =>
                toggleHandler(sectionKey, item.id, item.enabled || false)
              }
            >
              {item.label}
            </KibChipFilter>
          ) : (
            <KibChipFilter
              className={styles.chipFilter}
              disabled={item.disabled}
              enabled={item.enabled}
              key={item.id}
              toggle={() =>
                toggleHandler(sectionKey, item.id, item.enabled || false)
              }
            >
              {item.label}
            </KibChipFilter>
          )
        })}
      </div>
    </div>
  )
}

export default FilterSection
